// A few words for each land capability / classification level, so a grade is not
// shown to a farmer as a bare number.
//
// Davin's call, 21/08/2026: "instead of just the number grade add the mini text
// description beside it, each source has a few words associated to their levels."
// He is right that each source publishes its own wording, so nothing here is
// invented: England's descriptors are the ones already carried on the legend
// swatches in legend-metadata.js, and Scotland's are shortened from the Scottish
// Government's own national LCA page (the same page the legend links out to,
// read 20/08/2026).
//
// DRY, and the reason this is a separate module: legend-metadata.js states the
// same knowledge in a different shape, as "Grade 1 - excellent" swatch labels.
// Those labels should be generated from this table rather than restating it, but
// splitting a display string on " - " to get there is fragile, so the swatch
// labels are left alone for now. Housekeeping, tracked for a batch rather than
// fixed here.
//
// Wording confirmed 11/09/2026.

// Scotland, Land Capability for Agriculture. Hutton subdivide classes 3 to 6,
// and the published page distinguishes 3.1 from 3.2 explicitly but does not give
// separate wording for each of 4.1/4.2, 5.1/5.2/5.3 and 6.1/6.2/6.3 beyond
// "varying maintenance difficulty" and "varying plant quality". So the
// subdivisions fall back to their parent class's words rather than being told
// apart on a guess. Confirmed 11/09/2026: left exactly as it is. The legend
// carries whole classes 1 to 7 only, so the repetition is not visible there;
// the Land characteristics panel renders whatever LCCODE returns, which the
// layer comment says is a number such as 3.2, and that surface has not been
// looked at on a Scottish zone.
const LCA_SCOTLAND = {
  '1': 'very wide range of crops',
  '2': 'wide range of crops',
  '3': 'good to average arable',
  '3.1': 'high yields, narrow range',
  '3.2': 'average production',
  '4': 'mostly grass',
  '4.1': 'narrow range, mostly grass',
  '4.2': 'mostly grass',
  '5': 'improved grassland',
  '5.1': 'improved grassland',
  '5.2': 'improved grassland',
  '5.3': 'improved grassland',
  '6': 'rough grazing',
  '6.1': 'rough grazing',
  '6.2': 'rough grazing',
  '6.3': 'rough grazing',
  '7': 'very limited farming value',
};

// England, Provisional Agricultural Land Classification. Taken from the swatch
// labels already in legend-metadata.js, so the panel and the legend say the same
// thing about the same grade.
const ALC_ENGLAND = {
  '1': 'excellent',
  '2': 'very good',
  '3': 'good to moderate',
  '3a': 'good',
  '3b': 'moderate',
  '4': 'poor',
  '5': 'very poor',
};

// Wales, Predictive ALC. Its OWN table, not England's, because the two differ in
// shape and not only in wording: England returns a bare Grade 3 and never 3a or
// 3b, Wales returns 3a and 3b and never a bare 3. Confirmed by Davin against uat
// and a local build, 11/09/2026. Sharing one table implied each nation splits
// Grade 3 the way the other does.
//
// Words from Natural Resources Wales' published ALC definitions, so 3a reads
// "good to moderate" here where England's undivided Grade 3 does.
const ALC_WALES = {
  '1': 'excellent',
  '2': 'very good',
  '3a': 'good to moderate',
  '3b': 'moderate',
  '4': 'poor',
  '5': 'very poor',
};

// The fuller sentence NRW publish for each grade, shown for the DOMINANT class
// in Land characteristics only. The short words above still carry the legend and
// every minority class in a zone, because a sentence per class would turn a five
// row panel into a wall of text (Davin, 11/09/2026).
//
// Wales only. England and Scotland have no sentences in hand and deliberately
// keep short descriptors everywhere.
const ALC_WALES_SENTENCES = {
  '1': 'Very minor or no limitations; high and consistent yields for a wide range of crops.',
  '2': 'Minor limitations that affect crop flexibility or harvesting levels.',
  '3a':
    'Capable of consistently producing moderate yields of a narrow range of crops or high yields of grass/cereals.',
  '3b': 'Capable of producing moderate yields of narrow crops or high yields of grass.',
  '4': 'Severe limitations; mostly suited to rough grazing or low-yield crops.',
  '5': 'Very severe limitations; restricted mostly to permanent rough grazing or forestry.',
};

const SENTENCES_BY_LAYER = {
  alcWales: ALC_WALES_SENTENCES,
};

const BY_LAYER = {
  lcaScotland: LCA_SCOTLAND,
  alcEngland: ALC_ENGLAND,
  alcWales: ALC_WALES,
};

// Classes arrive with the provider's own code: "3.1" from Hutton, "Grade 3" from
// Natural England, and "Grade 5" from Wales after normalisation. Urban,
// Non-agricultural and anything unrecognised return null rather than a guess, and
// the row then shows the code alone exactly as it does today.
export function landClassDescription(layerId, code) {
  return lookup(BY_LAYER[layerId], code);
}

/**
 * The fuller published sentence for a class, or null where none exists.
 *
 * Wales only today. Returns null for England and Scotland rather than falling
 * back to the short descriptor, so a caller can tell "no sentence published"
 * from "sentence that happens to be short" and render accordingly.
 *
 * @param {string} layerId
 * @param {string|number} code the provider's own class code
 * @returns {string|null}
 */
export function landClassSentence(layerId, code) {
  return lookup(SENTENCES_BY_LAYER[layerId], code);
}

// Shared key normalisation, so a sentence can never be found under a spelling
// the descriptor is not, or the other way round.
function lookup(table, code) {
  if (!table || code == null) return null;
  const key = String(code)
    .trim()
    .toLowerCase()
    .replace(/^(grade|class)\s+/, '');
  return table[key] || null;
}
